/**
 * Integrytas Group — Create New Language (CSM.I18N.NEW_LANGUAGE).
 *
 * Activate a language that isn't yet defined and seed its translations: data
 * translations are copied from the home language as placeholders (via
 * csm_i18n_copy_data_strings); screen prompts fall back to the static defaults
 * until translated. An optional AI-translate pass machine-translates all prompts.
 * Finish localising on the Translations screen (CSM.I18N.MANAGER).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PageComponentProps } from '../../pages/page_components';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { getForensicActorId } from '../../types/auth';
import { PersonalizationProvider, PersonalizationToggle } from '../../components/personalization';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { PROMPT_KEYS, promptDefault } from '../../lib/i18n_flatten';
import { aiClient } from '../../lib/ai/ai_client';
import { localeName } from '../../i18n/locale_display';
import { LOCALE_CATALOG } from '../../i18n/locale_catalog';


export function I18nNewLanguagePage({ session }: PageComponentProps) {
  return (
    <PersonalizationProvider pageCode="CSM.I18N.NEW_LANGUAGE" session={session}>
      <I18nNewLanguagePageInner session={session} />
    </PersonalizationProvider>
  );
}

function I18nNewLanguagePageInner({ session }: PageComponentProps) {
  const { t } = useTranslation();
  // §8: source/home language for placeholders follows the ACTIVE UI locale.
  const home = useActiveLocale() || 'EN_US';
  const [active, setActive] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [aiTranslate, setAiTranslate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = async () => {
    const { data } = await apiDb.from('csm_language_master').select('internal_code').eq('is_active', 'Y');
    setActive(((data ?? []) as { internal_code: string }[]).map((r) => r.internal_code));
  };
  useEffect(() => { void reload(); }, []);

  const available = useMemo(() => LOCALE_CATALOG.filter((c) => !active.includes(c)), [active]);

  const create = async () => {
    const c = code.trim().toUpperCase();
    // LL_CC, optionally with a variant segment (EN_GB_SCOT — Scottish Standard
    // English, whose BCP-47 tag is too long to store). 10 chars is the width of
    // every locale column in the schema, so anything longer would be truncated.
    if (!/^[A-Z]{2}_[A-Z]{2}(_[A-Z]{2,4})?$/.test(c) || c.length > 10) {
      setError(t('i18n_new.err.format', 'Use a locale code like EN_GB, or EN_GB_SCOT for a variant.')); return;
    }
    if (active.includes(c)) { setError(t('i18n_new.err.already_active', 'That language is already active.')); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const actor = getForensicActorId(session);
      await apiDb.from('csm_language_master').upsert({ internal_code: c, is_active: 'Y', modified_by: actor }, { onConflict: 'internal_code' });
      const { data: copied } = await apiDb.rpc<number>('csm_i18n_copy_data_strings', { p_from: home, p_to: c });
      let msg = `Language ${c} created (${copied ?? 0} data translations copied from ${home}).`;
      if (aiTranslate) {
        const CHUNK = 60;
        let done = 0;
        for (let i = 0; i < PROMPT_KEYS.length; i += CHUNK) {
          const keys = PROMPT_KEYS.slice(i, i + CHUNK);
          const texts = keys.map((k) => promptDefault(k, 'EN_US'));
          const { data: res, error: e } = await aiClient.translate(texts, c);
          if (e || !res?.translations?.length) { msg += ` AI-translate stopped: ${e?.message ?? 'no result'}.`; break; }
          const rows = keys.map((k, j) => ({
            locale_code: c, string_key: k,
            display_text: res.translations[j] ?? promptDefault(k, 'EN_US'),
            is_machine_translated: 'Y', modified_by: actor,
          }));
          await apiDb.from('csm_i18n_string').upsert(rows, { onConflict: 'locale_code,string_key' });
          done += keys.length;
        }
        if (done) msg += ` ${done} screen prompts machine-translated.`;
      } else {
        msg += ' Open Translations to localise screen prompts (AI-translate available there).';
      }
      setNotice(msg);
      setCode('');
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('i18n_new.title', 'Create Language')}</span>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('i18n_new.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <ErrorBanner
          message={t('i18n_new.error_banner', "Couldn't create the language.")}
          detail={error}
          details_label={t('common.details', 'Details')}
        />
      )}
      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-xl rounded border border-csm-border bg-white p-4">
          <p className="mb-3 text-csm-sm text-csm-ink-soft">
            Add a language to this app. Data translations are copied from your home language
            (<span>{localeName(home, home)}</span>) as starting points; screen prompts fall back to the
            defaults until translated. Finish localising on the <span className="font-medium">Translations</span> screen.
          </p>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('i18n_new.language_label', 'Language')}</label>
          <input list="csm-locale-catalog" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="EN_GB" className="mb-1 h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" />
          <datalist id="csm-locale-catalog">
            {available.map((c) => <option key={c} value={c}>{localeName(c, home)}</option>)}
          </datalist>
          <div className="mb-3 text-csm-2xs text-csm-muted">{t('i18n_new.format_hint', 'Format: LANG_REGION (e.g. EN_GB, ES_MX, FR_FR), optionally with a variant (EN_GB_SCOT).')}</div>
          <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
            <input type="checkbox" checked={aiTranslate} onChange={(e) => setAiTranslate(e.target.checked)} className="h-4 w-4 accent-gray-700" />
            {t('i18n_new.ai_translate', 'AI-translate all screen prompts now (may take a while; needs an AI key configured)')}
          </label>
          <button onClick={() => void create()} disabled={busy || !code.trim()}
            className="rounded border border-primary bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
            {busy ? t('i18n_new.creating', 'Creating…') : t('i18n_new.create', 'Create language')}
          </button>
          {notice && <div className="mt-3 rounded border border-primary/30 bg-primary/5 px-2 py-1 text-csm-sm text-primary">{notice}</div>}
          <div className="mt-4 text-csm-xs text-csm-watermark">{t('i18n_new.active_languages', 'Active languages:')} {active.map(c => localeName(c, c)).join(', ') || '—'}</div>
        </div>
      </div>
    </div>
  );
}
